import { useState, useEffect, useCallback, useRef } from "react";
import idleTimeout from "idle-timeout";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";

// 1. User is inactive for `timeUntilPrompt` (default 30 min).
// 2. The prompt appears and counts down for `promptDuration` (default 1 min).
// 3. If no action is taken, handleLogout is called automatically.
const IDLE_TIME_UNTIL_PROMPT = 30 * 60 * 1000; // 30 minutes until prompt appears
const PROMPT_DURATION = 1 * 60 * 1000; // 1 minute prompt/countdown duration

/**
 * A component that monitors user activity and shows a timeout warning dialog.
 * If the user does not interact, it will call the handleLogout function.
 * Uses the idle-timeout npm package for robust timeout handling including system sleep/wake scenarios.
 */
const IdleTimeoutContainer = ({
  handleLogout,
  timeUntilPrompt = IDLE_TIME_UNTIL_PROMPT,
  promptDuration = PROMPT_DURATION,
}) => {
  const [isPrompted, setIsPrompted] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const idleTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const countdownStartTimeRef = useRef(null);

  // Initialize idle timer
  useEffect(() => {
    const idleInstance = idleTimeout(
      () => {
        // Callback when user becomes idle
        setIsPrompted(true);
        countdownStartTimeRef.current = Date.now();
        const seconds = Math.round(promptDuration / 1000);
        setRemaining(seconds);

        // Start countdown interval
        countdownIntervalRef.current = setInterval(() => {
          const elapsed = Date.now() - countdownStartTimeRef.current;
          const secondsRemaining = Math.max(
            0,
            Math.round((promptDuration - elapsed) / 1000),
          );
          setRemaining(secondsRemaining);

          // Auto-logout when countdown reaches 0
          if (secondsRemaining <= 0) {
            clearInterval(countdownIntervalRef.current);
            setIsPrompted(false);
            handleLogout?.();
          }
        }, 500);
      },
      {
        element: document,
        timeout: timeUntilPrompt,
        loop: false, // Don't loop the timeout
      },
    );

    idleTimerRef.current = idleInstance;

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
      if (idleTimerRef.current) {
        idleTimerRef.current.destroy();
      }
    };
  }, [timeUntilPrompt, promptDuration, handleLogout]);

  const handleStayLoggedIn = useCallback(() => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }
    setIsPrompted(false);
    // Reset the idle timer
    if (idleTimerRef.current) {
      idleTimerRef.current.reset();
    }
  }, []);

  const handleImmediateLogout = useCallback(() => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }
    setIsPrompted(false);
    handleLogout?.();
  }, [handleLogout]);

  // This component only renders the dialog when the user is prompted.
  if (!isPrompted) {
    return null;
  }

  const totalSeconds = Math.round(promptDuration / 1000);

  return (
    <Dialog open={isPrompted} onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-sm w-[calc(100%-2rem)] rounded-xl p-0 overflow-hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="items-center text-center gap-3 px-6 pt-6">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-amber-50 dark:bg-amber-950">
            <AlertTriangle className="w-6 h-6 text-amber-500 dark:text-amber-400" />
          </div>
          <DialogTitle className="text-base font-semibold text-slate-900 dark:text-slate-100">
            Session Expiring Soon
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
            You&apos;ve been inactive for a while. You will be logged out
            automatically in{" "}
            <span className="font-semibold text-amber-500">{remaining}s</span>.
          </DialogDescription>
        </DialogHeader>

        {/* Countdown progress bar */}
        <div className="mx-6 mt-1 mb-2 h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
          <div
            className="h-full rounded-full bg-amber-400 transition-all duration-1000 ease-linear"
            style={{ width: `${(remaining / totalSeconds) * 100}%` }}
          />
        </div>

        <DialogFooter className="flex-col-reverse sm:flex-row gap-2 px-6 pb-6 pt-2">
          <Button
            variant="outline"
            onClick={handleImmediateLogout}
            className="w-full sm:w-auto cursor-pointer border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            Log Out
          </Button>
          <Button
            onClick={handleStayLoggedIn}
            className="w-full sm:w-auto cursor-pointer font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
          >
            Stay Logged In
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default IdleTimeoutContainer;
